import React, { useEffect, useRef, useState } from 'react';
import { X, TrendingUp, Eye, MonitorSmartphone, RefreshCw, Rotate3d } from 'lucide-react';
import './PolandBuilding3D.css';

/* ════════════════════════════════════════════════════════════
   3D building view (PRS page). The colour-coded tower lets a
   manager spot an over/under-heated apartment at a glance. Three
   .js (r128) is lazy-loaded from CDN only when this section
   mounts, so it never weighs down the main bundle. The scene
   logic is a faithful port of the climapri-3d-demo mockup; the
   temperature-range slider drives the colours live.
   ════════════════════════════════════════════════════════════ */

/**
 * three.js is self-hosted rather than pulled from cdnjs. A third-party script
 * on every page carrying the viewer meant an extra DNS + TLS handshake before
 * the scene could start, a dependency on someone else's uptime, and a
 * cross-origin request that sits awkwardly next to the site's GDPR claims.
 * Served from our own origin it reuses the existing connection and is covered
 * by our cache headers.
 *
 * Pinned to r128: the scene code below uses the pre-modularisation `THREE.*`
 * global API, which later revisions changed. Do not "upgrade" the file without
 * porting the scene.
 */
const THREE_URL = '/js/three.r128.min.js';
let threePromise: Promise<any> | null = null;
function loadThree(): Promise<any> {
  if ((window as any).THREE) return Promise.resolve((window as any).THREE);
  if (threePromise) return threePromise;
  threePromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = THREE_URL;
    s.async = true;
    s.onload = () => resolve((window as any).THREE);
    s.onerror = () => reject(new Error('Failed to load three.js'));
    document.head.appendChild(s);
  });
  return threePromise;
}

/**
 * Every user-facing string in the viewer. Defaults are Polish so the existing
 * Polish pages keep working unchanged; the Swedish pages pass a translated set
 * (see components/sweden/SwedenBuilding3D.tsx). The scene itself is language
 * agnostic — apartment labels are numbers and already use a comma decimal
 * separator, which is correct for both markets.
 */
export type Building3DLabels = {
  badge: string;
  visualisations: string;
  temperature: string;
  rangeLabel: string;
  legendCold: string;
  legendInRange: string;
  legendWarm: string;
  legendHot: string;
  hint: string;
  /** Tooltip heading, receives the apartment name e.g. "A42". */
  apartment: (name: string) => string;
  statusInRange: string;
  statusUnderheated: string;
  statusBelowRange: string;
  statusOverheated: string;
  statusAboveRange: string;
};

const PL_LABELS: Building3DLabels = {
  badge: 'Widok demonstracyjny · Osiedle Wisła',
  visualisations: 'Wizualizacje',
  temperature: 'Temperatura',
  rangeLabel: 'Dopuszczalny zakres temperatur',
  legendCold: 'Zimno',
  legendInRange: 'W zakresie',
  legendWarm: 'Ciepło',
  legendHot: 'Gorąco',
  hint: 'Przeciągnij, aby obrócić · Scroll, aby przybliżyć',
  apartment: (name) => `Mieszkanie ${name}`,
  statusInRange: 'W zakresie',
  statusUnderheated: 'Niedogrzane',
  statusBelowRange: 'Poniżej zakresu',
  statusOverheated: 'Przegrzane',
  statusAboveRange: 'Powyżej zakresu',
};

const PolandBuilding3D: React.FC<{ labels?: Building3DLabels }> = ({ labels = PL_LABELS }) => {
  // Read through a ref so the imperative three.js closure sees current labels
  // without the effect having to re-run (and rebuild the whole scene).
  const labelsRef = useRef(labels);
  labelsRef.current = labels;
  // Unique per instance: the checkbox id used to be hardcoded, which would
  // collide if two viewers ever rendered on the same page.
  const checkboxId = React.useId();
  const sceneRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const handleLowRef = useRef<HTMLDivElement>(null);
  const handleHighRef = useRef<HTMLDivElement>(null);
  const rangeLowRef = useRef<HTMLSpanElement>(null);
  const rangeHighRef = useRef<HTMLSpanElement>(null);
  const hintRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  // The controls panel is closed on phones, where it would cover most of the
  // (smaller) building view, and open on larger screens. The decision is made
  // after mount: reading window.innerWidth during the first render made the
  // server-rendered HTML and the client disagree, which is a hydration error
  // (React #418) on every mobile visit.
  const [panelOpen, setPanelOpen] = useState(true);
  useEffect(() => {
    if (window.innerWidth <= 760) setPanelOpen(false);
  }, []);
  // "Temperatura" visualisation toggle. When off, the range chart is hidden
  // and the tower falls back to neutral (no temperature colouring). A ref
  // mirrors the state so the imperative three.js closure can read it, and
  // recolorRef lets the checkbox handler trigger a repaint on demand.
  const [showTemp, setShowTemp] = useState(true);
  const showTempRef = useRef(true);
  const recolorRef = useRef<() => void>(() => {});

  useEffect(() => {
    const container = sceneRef.current;
    if (!container) return;

    let cancelled = false;
    let raf = 0;
    let hintTimeout = 0;
    let renderer: any = null;
    const listeners: Array<[EventTarget, string, EventListener, any]> = [];
    const on = (t: EventTarget, type: string, fn: EventListener, opts?: any) => {
      t.addEventListener(type, fn, opts);
      listeners.push([t, type, fn, opts]);
    };

    loadThree().then((THREE: any) => {
      if (cancelled || !sceneRef.current) return;

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xEFF2F6);
      const w = container.clientWidth;
      const h = container.clientHeight;
      const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 1000);

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(w, h);
      container.appendChild(renderer.domElement);

      scene.add(new THREE.AmbientLight(0xffffff, 0.85));
      const dir = new THREE.DirectionalLight(0xffffff, 0.4); dir.position.set(10, 20, 15); scene.add(dir);
      const dir2 = new THREE.DirectionalLight(0xffffff, 0.2); dir2.position.set(-10, 5, -15); scene.add(dir2);

      const tempColor = (temp: number, lo: number, hi: number) => {
        if (temp >= lo && temp <= hi) return new THREE.Color(0xC8CDD4);
        if (temp < lo) {
          const t = Math.max(0, Math.min(1, (lo - temp) / 5));
          return t < 0.5
            ? new THREE.Color(0x6B8AD9).lerp(new THREE.Color(0x2A53C0), t * 2)
            : new THREE.Color(0x2A53C0).lerp(new THREE.Color(0x102B7A), (t - 0.5) * 2);
        }
        const t = Math.max(0, Math.min(1, (temp - hi) / 6));
        if (t < 0.33) return new THREE.Color(0xC4439F).lerp(new THREE.Color(0xB12A8E), t * 3);
        if (t < 0.66) return new THREE.Color(0xB12A8E).lerp(new THREE.Color(0xA01A6B), (t - 0.33) * 3);
        return new THREE.Color(0xA01A6B).lerp(new THREE.Color(0xD4264A), (t - 0.66) * 3);
      };

      const makeRoundedBox = (size: number, radius: number, segments: number) => {
        const shape = new THREE.Shape();
        const half = size / 2;
        shape.absarc(-half + radius, -half + radius, radius, Math.PI, Math.PI * 1.5);
        shape.lineTo(half - radius, -half);
        shape.absarc(half - radius, -half + radius, radius, Math.PI * 1.5, 0);
        shape.lineTo(half, half - radius);
        shape.absarc(half - radius, half - radius, radius, 0, Math.PI * 0.5);
        shape.lineTo(-half + radius, half);
        shape.absarc(-half + radius, half - radius, radius, Math.PI * 0.5, Math.PI);
        shape.lineTo(-half, -half + radius);
        const geo = new THREE.ExtrudeGeometry(shape, {
          steps: 1, depth: size - radius * 2, bevelEnabled: true,
          bevelThickness: radius, bevelSize: radius, bevelOffset: 0, bevelSegments: segments, curveSegments: segments,
        });
        geo.translate(0, 0, -(size - radius * 2) / 2);
        return geo;
      };

      const makeLabelSprite = (name: string, temp: number) => {
        const canvas = document.createElement('canvas');
        canvas.width = 512; canvas.height = 512;
        const ctx = canvas.getContext('2d')!;
        ctx.clearRect(0, 0, 512, 512);
        ctx.fillStyle = 'rgba(20, 5, 35, 0.92)';
        ctx.font = '700 150px -apple-system, "Segoe UI", sans-serif';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(name, 256, 200);
        ctx.font = '600 110px -apple-system, "Segoe UI", sans-serif';
        ctx.fillStyle = 'rgba(20, 5, 35, 0.88)';
        ctx.fillText(`${temp.toFixed(1).replace('.', ',')}°C`, 256, 340);
        const tex = new THREE.CanvasTexture(canvas);
        tex.minFilter = THREE.LinearFilter; tex.magFilter = THREE.LinearFilter; tex.anisotropy = 8; tex.needsUpdate = true;
        return tex;
      };

      let tempRangeLow = 18.0;
      let tempRangeHigh = 21.0;

      const apartments: any[] = [];
      const aptMeshes: any[] = [];
      const buildingGroup = new THREE.Group();
      const aptSize = 2.6;
      const aptGap = 0.06;
      const floors = 9;
      const wingLong = 8;
      const positions: Array<{ col: number; row: number }> = [];
      for (let i = 0; i < wingLong; i++) { positions.push({ col: i, row: 0 }); positions.push({ col: i, row: 1 }); }
      const buildingWidth = wingLong * (aptSize + aptGap) - aptGap;
      const buildingDepth = 2 * (aptSize + aptGap) - aptGap;
      const offsetX = -buildingWidth / 2 + aptSize / 2;
      const offsetZ = -buildingDepth / 2 + aptSize / 2;

      let aptCounter = 0;
      for (let floor = 0; floor < floors; floor++) {
        for (let pi = 0; pi < positions.length; pi++) {
          const p = positions[pi];
          aptCounter++;
          const x = offsetX + p.col * (aptSize + aptGap);
          const z = offsetZ + p.row * (aptSize + aptGap);
          const y = floor * (aptSize + aptGap) + aptSize / 2;
          let temp = 19.5 + (Math.random() - 0.5) * 8.0;
          if (floor >= floors - 3) temp += (floor - (floors - 4)) * 0.7;
          if (floor <= 1) temp -= (2 - floor) * 0.6;
          temp = Math.round(temp * 10) / 10;
          apartments.push({ name: `A${aptCounter}`, temp, baseTemp: temp, floor, x, y, z });
        }
      }

      const roundedGeo = makeRoundedBox(aptSize, 0.08, 3);
      const labelGeo = new THREE.PlaneGeometry(aptSize * 0.92, aptSize * 0.92);

      apartments.forEach(apt => {
        const material = new THREE.MeshLambertMaterial({ color: tempColor(apt.temp, tempRangeLow, tempRangeHigh) });
        const mesh = new THREE.Mesh(roundedGeo, material);
        mesh.position.set(apt.x, apt.y, apt.z);
        mesh.userData = apt;
        const labelMat = new THREE.MeshBasicMaterial({ map: makeLabelSprite(apt.name, apt.temp), transparent: true, depthWrite: false, side: THREE.DoubleSide });
        const faces: Array<[number, number, number, number]> = [
          [0, 0, aptSize / 2 + 0.005, 0],
          [0, 0, -aptSize / 2 - 0.005, Math.PI],
          [-aptSize / 2 - 0.005, 0, 0, -Math.PI / 2],
          [aptSize / 2 + 0.005, 0, 0, Math.PI / 2],
        ];
        faces.forEach(([px, py, pz, ry]) => {
          const lbl = new THREE.Mesh(labelGeo, labelMat);
          lbl.position.set(px, py, pz);
          lbl.rotation.y = ry;
          mesh.add(lbl);
        });
        buildingGroup.add(mesh);
        aptMeshes.push(mesh);
      });
      scene.add(buildingGroup);

      const neutralColor = new THREE.Color(0xC8CDD4);
      const updateColors = () => {
        aptMeshes.forEach(mesh => {
          mesh.material.color = showTempRef.current
            ? tempColor(mesh.userData.temp, tempRangeLow, tempRangeHigh)
            : neutralColor;
        });
      };
      recolorRef.current = updateColors;

      let isDragging = false;
      let autoRotate = true;
      let prevX = 0, prevY = 0;
      let rotY = -0.7, rotX = 0.32, camDistance = 40;
      const lookHeight = 12;
      const updateCamera = () => {
        const cx = Math.sin(rotY) * Math.cos(rotX) * camDistance;
        const cy = Math.sin(rotX) * camDistance + lookHeight;
        const cz = Math.cos(rotY) * Math.cos(rotX) * camDistance;
        camera.position.set(cx, cy, cz);
        camera.lookAt(0, lookHeight, 0);
      };
      updateCamera();

      const fadeHint = () => hintRef.current && hintRef.current.classList.add('fade');

      on(container, 'mousedown', ((e: MouseEvent) => { isDragging = true; autoRotate = false; prevX = e.clientX; prevY = e.clientY; fadeHint(); }) as EventListener);
      on(window, 'mouseup', (() => { isDragging = false; }) as EventListener);
      on(window, 'mousemove', ((e: MouseEvent) => {
        if (!isDragging) return;
        rotY -= (e.clientX - prevX) * 0.008;
        rotX += (e.clientY - prevY) * 0.005;
        rotX = Math.max(-0.2, Math.min(1.2, rotX));
        prevX = e.clientX; prevY = e.clientY;
        updateCamera();
      }) as EventListener);

      on(container, 'touchstart', ((e: TouchEvent) => {
        if (e.touches.length === 1) { isDragging = true; autoRotate = false; prevX = e.touches[0].clientX; prevY = e.touches[0].clientY; fadeHint(); }
      }) as EventListener, { passive: true });
      on(container, 'touchend', (() => { isDragging = false; }) as EventListener);
      on(container, 'touchmove', ((e: TouchEvent) => {
        if (!isDragging || e.touches.length !== 1) return;
        rotY -= (e.touches[0].clientX - prevX) * 0.008;
        rotX += (e.touches[0].clientY - prevY) * 0.005;
        rotX = Math.max(-0.2, Math.min(1.2, rotX));
        prevX = e.touches[0].clientX; prevY = e.touches[0].clientY;
        updateCamera();
      }) as EventListener, { passive: true });

      on(container, 'wheel', ((e: WheelEvent) => {
        e.preventDefault();
        autoRotate = false; fadeHint();
        camDistance = Math.max(20, Math.min(140, camDistance + e.deltaY * 0.06));
        updateCamera();
      }) as EventListener, { passive: false });

      const raycaster = new THREE.Raycaster();
      const mouse = new THREE.Vector2();
      on(container, 'mousemove', ((e: MouseEvent) => {
        const tooltip = tooltipRef.current;
        if (!tooltip) return;
        if (isDragging) { tooltip.style.display = 'none'; return; }
        const rect = container.getBoundingClientRect();
        mouse.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        raycaster.setFromCamera(mouse, camera);
        const hits = raycaster.intersectObjects(aptMeshes);
        if (hits.length > 0) {
          const apt = hits[0].object.userData;
          tooltip.style.display = 'block';
          tooltip.style.left = `${e.clientX - rect.left + 14}px`;
          tooltip.style.top = `${e.clientY - rect.top + 14}px`;
          const L = labelsRef.current;
          (tooltip.querySelector('.t-name') as HTMLElement).textContent = L.apartment(apt.name);
          (tooltip.querySelector('.t-temp') as HTMLElement).textContent = `${apt.temp.toFixed(1).replace('.', ',')} °C`;
          let status = L.statusInRange;
          if (apt.temp < tempRangeLow - 2) status = L.statusUnderheated;
          else if (apt.temp < tempRangeLow) status = L.statusBelowRange;
          else if (apt.temp > tempRangeHigh + 3) status = L.statusOverheated;
          else if (apt.temp > tempRangeHigh) status = L.statusAboveRange;
          (tooltip.querySelector('.t-status') as HTMLElement).textContent = status;
        } else {
          tooltip.style.display = 'none';
        }
      }) as EventListener);

      const animate = () => {
        raf = requestAnimationFrame(animate);
        if (autoRotate) { rotY -= 0.0022; updateCamera(); }
        renderer.render(scene, camera);
      };
      animate();

      on(window, 'resize', (() => {
        if (!sceneRef.current) return;
        const cw = container.clientWidth, ch = container.clientHeight;
        camera.aspect = cw / ch; camera.updateProjectionMatrix(); renderer.setSize(cw, ch);
      }) as EventListener);

      // ── range slider ──
      const slider = sliderRef.current, handleLow = handleLowRef.current, handleHigh = handleHighRef.current;
      const rangeLowEl = rangeLowRef.current, rangeHighEl = rangeHighRef.current;
      const pctToTemp = (pct: number) => 15 + (pct / 100) * 15;
      const tempToPct = (temp: number) => ((temp - 15) / 15) * 100;
      let dragHandle: 'low' | 'high' | null = null;

      const startDrag = (which: 'low' | 'high') => ((e: Event) => { e.preventDefault(); dragHandle = which; }) as EventListener;
      if (handleLow) { on(handleLow, 'mousedown', startDrag('low')); on(handleLow, 'touchstart', startDrag('low'), { passive: false }); }
      if (handleHigh) { on(handleHigh, 'mousedown', startDrag('high')); on(handleHigh, 'touchstart', startDrag('high'), { passive: false }); }

      const onDragMove = (clientX: number) => {
        if (!dragHandle || !slider) return;
        const rect = slider.getBoundingClientRect();
        let pct = ((clientX - rect.left) / rect.width) * 100;
        pct = Math.max(0, Math.min(100, pct));
        const temp = Math.round(pctToTemp(pct) * 10) / 10;
        if (dragHandle === 'low') {
          if (temp < tempRangeHigh - 0.5) {
            tempRangeLow = temp;
            if (handleLow) handleLow.style.left = `${pct}%`;
            if (rangeLowEl) rangeLowEl.textContent = temp.toFixed(1).replace('.', ',');
          }
        } else if (temp > tempRangeLow + 0.5) {
          tempRangeHigh = temp;
          if (handleHigh) handleHigh.style.left = `${pct}%`;
          if (rangeHighEl) rangeHighEl.textContent = temp.toFixed(1).replace('.', ',');
        }
        updateColors();
      };
      on(window, 'mousemove', ((e: MouseEvent) => onDragMove(e.clientX)) as EventListener);
      on(window, 'mouseup', (() => { dragHandle = null; }) as EventListener);
      on(window, 'touchmove', ((e: TouchEvent) => { if (dragHandle && e.touches.length === 1) onDragMove(e.touches[0].clientX); }) as EventListener, { passive: true });
      on(window, 'touchend', (() => { dragHandle = null; }) as EventListener);

      if (handleLow) handleLow.style.left = `${tempToPct(tempRangeLow)}%`;
      if (handleHigh) handleHigh.style.left = `${tempToPct(tempRangeHigh)}%`;
      if (rangeLowEl) rangeLowEl.textContent = tempRangeLow.toFixed(1).replace('.', ',');
      if (rangeHighEl) rangeHighEl.textContent = tempRangeHigh.toFixed(1).replace('.', ',');

      hintTimeout = window.setTimeout(fadeHint, 6000);
    }).catch(() => { /* CDN unavailable — section degrades gracefully to an empty viewer */ });

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(hintTimeout);
      listeners.forEach(([t, type, fn, opts]) => t.removeEventListener(type, fn, opts));
      if (renderer) {
        try { renderer.dispose(); } catch { /* noop */ }
        if (renderer.domElement && renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="phr-b3d">
      <div className="b3d-scene" ref={sceneRef} />

      <div className="b3d-badge"><span className="dot" /> {labels.badge}</div>

      <button className="icon-btn" type="button" title={labels.visualisations} onClick={() => setPanelOpen(o => !o)}>
        <TrendingUp size={20} />
      </button>

      {panelOpen && (
        <div className="panel">
          <div className="panel-header">
            <div className="panel-title"><TrendingUp size={16} color="#185FA5" /> {labels.visualisations}</div>
            <span className="panel-close" onClick={() => setPanelOpen(false)}><X size={16} /></span>
          </div>
          <div className="panel-body">
            <div className="control-group">
              <div className="check-row">
                <input
                  type="checkbox"
                  id={checkboxId}
                  checked={showTemp}
                  onChange={e => {
                    const v = e.target.checked;
                    setShowTemp(v);
                    showTempRef.current = v;
                    recolorRef.current();
                  }}
                />
                <label htmlFor={checkboxId}>{labels.temperature}</label>
              </div>
              {/* kept mounted (display toggled) so the slider's drag
                  listeners set up on load survive re-checking the box */}
              <div className="temp-controls" style={{ display: showTemp ? undefined : 'none' }}>
                <div className="range-label">{labels.rangeLabel}</div>
                <div className="range-values">
                  <span className="range-pill" ref={rangeLowRef}>18,0</span>
                  <span className="range-pill warm" ref={rangeHighRef}>21,0</span>
                </div>
                <div className="slider-track" ref={sliderRef}>
                  <div className="slider-handle" ref={handleLowRef} style={{ left: '30%' }} />
                  <div className="slider-handle" ref={handleHighRef} style={{ left: '51%' }} />
                </div>
                <div className="slider-ticks"><span>15°</span><span>20°</span><span>25°</span><span>30°</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="legend">
        <div className="legend-item"><span className="legend-swatch" style={{ background: '#2A53C0' }} /><span>{labels.legendCold}</span></div>
        <div className="legend-item"><span className="legend-swatch" style={{ background: '#C8CDD4' }} /><span>{labels.legendInRange}</span></div>
        <div className="legend-item"><span className="legend-swatch" style={{ background: '#B12A8E' }} /><span>{labels.legendWarm}</span></div>
        <div className="legend-item"><span className="legend-swatch" style={{ background: '#D4264A' }} /><span>{labels.legendHot}</span></div>
      </div>

      <div className="hint" ref={hintRef}><Rotate3d size={15} /> {labels.hint}</div>

      <div className="b3d-tooltip" ref={tooltipRef}>
        <div className="t-name" />
        <div className="t-temp" />
        <div className="t-status" />
      </div>
    </div>
  );
};

export const PolandBuilding3DBenefits: React.FC = () => (
  <div className="phr-b3d-benefits">
    <div className="phr-b3d-benefit">
      <span className="b3d-bic"><Eye size={22} /></span>
      <h3>Od razu widzisz problem</h3>
      <p>Przegrzane lub wychłodzone mieszkania wyróżniają się kolorem. Wszystko w zakresie pozostaje szare.</p>
    </div>
    <div className="phr-b3d-benefit">
      <span className="b3d-bic"><MonitorSmartphone size={22} /></span>
      <h3>Bez wizyt w terenie</h3>
      <p>Pełen wgląd z komputera lub telefonu, gdziekolwiek jesteś.</p>
    </div>
    <div className="phr-b3d-benefit">
      <span className="b3d-bic"><RefreshCw size={22} /></span>
      <h3>Dane w czasie rzeczywistym</h3>
      <p>Temperatury aktualizują się na bieżąco. Wychwytujesz problem w chwili, gdy się pojawia.</p>
    </div>
  </div>
);

export default PolandBuilding3D;
